/**
 * Transcript Context – Dubai Police 999 Emergency Assistant
 *
 * Manages conversation transcript state globally.
 * Also persists messages to the backend when a conversation_id is available.
 */

import React, { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { config } from '../config/runtime-config';

export interface TranscriptMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  agentId?: string;
}

interface TranscriptContextValue {
  messages: TranscriptMessage[];
  conversationId: string | null;
  setConversationId: (id: string | null) => void;
  addMessage: (message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => void;
  clearTranscript: () => void;
  isPanelOpen: boolean;
  togglePanel: () => void;
  unreadCount: number;
  markAsRead: () => void;
}

const TranscriptContext = createContext<TranscriptContextValue | undefined>(undefined);

const MAX_MESSAGES = 200;
const STORAGE_KEY = 'dp_transcript';
const PANEL_STATE_KEY = 'dp_transcript_panel_open';

interface TranscriptProviderProps {
  children: ReactNode;
}

const BACKEND_URL = config.REACT_APP_BACKEND_URL;

async function persistMessage(conversationId: string, role: string, content: string) {
  if (!BACKEND_URL || !conversationId) return;
  try {
    await fetch(`${BACKEND_URL}/api/conversations/${conversationId}/messages`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role, content }),
    });
  } catch (err) {
    console.warn('[Transcript] Backend persist failed (non-critical):', err);
  }
}

export const TranscriptProvider: React.FC<TranscriptProviderProps> = ({ children }) => {
  const [messages, setMessages] = useState<TranscriptMessage[]>([]);
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [isPanelOpen, setIsPanelOpen] = useState<boolean>(() => {
    const saved = localStorage.getItem(PANEL_STATE_KEY);
    return saved ? JSON.parse(saved) : false;
  });
  const [unreadCount, setUnreadCount] = useState(0);

  // Load from localStorage on mount
  useEffect(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const parsed = JSON.parse(saved);
        setMessages(parsed.map((m: any) => ({ ...m, timestamp: new Date(m.timestamp) })));
      }
    } catch { localStorage.removeItem(STORAGE_KEY); }
  }, []);

  // Persist to localStorage
  useEffect(() => {
    try {
      if (messages.length > 0) localStorage.setItem(STORAGE_KEY, JSON.stringify(messages));
    } catch {}
  }, [messages]);

  // Persist panel state
  useEffect(() => {
    localStorage.setItem(PANEL_STATE_KEY, JSON.stringify(isPanelOpen));
  }, [isPanelOpen]);

  const addMessage = useCallback((message: Omit<TranscriptMessage, 'id' | 'timestamp'>) => {
    const newMessage: TranscriptMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      timestamp: new Date(),
    };

    setMessages(prev => {
      const updated = [...prev, newMessage];
      return updated.length > MAX_MESSAGES ? updated.slice(-MAX_MESSAGES) : updated;
    });

    if (!isPanelOpen) setUnreadCount(prev => prev + 1);

    // Persist to backend (fire-and-forget)
    if (conversationId) {
      persistMessage(conversationId, message.role, message.content);
    }
  }, [isPanelOpen, conversationId]);

  const clearTranscript = useCallback(() => {
    setMessages([]);
    setUnreadCount(0);
    setConversationId(null);
    localStorage.removeItem(STORAGE_KEY);
  }, []);

  const togglePanel = useCallback(() => {
    setIsPanelOpen(prev => {
      if (!prev) setUnreadCount(0);
      return !prev;
    });
  }, []);

  const markAsRead = useCallback(() => setUnreadCount(0), []);

  return (
    <TranscriptContext.Provider value={{
      messages,
      conversationId,
      setConversationId,
      addMessage,
      clearTranscript,
      isPanelOpen,
      togglePanel,
      unreadCount,
      markAsRead,
    }}>
      {children}
    </TranscriptContext.Provider>
  );
};

export const useTranscript = (): TranscriptContextValue => {
  const ctx = useContext(TranscriptContext);
  if (!ctx) throw new Error('useTranscript must be used within TranscriptProvider');
  return ctx;
};
